'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { toAnalyticsReasonCode, trackWalletAction } from '@workspace/analytics/posthog/events';
import { Button } from '@workspace/ui/components/button';
import { Combobox } from '@workspace/ui/components/custom/combobox';
import { DatePicker } from '@workspace/ui/components/custom/date-picker';
import { PhoneNumberSelect } from '@workspace/ui/components/custom/phone-input';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@workspace/ui/components/form';
import { Input } from '@workspace/ui/components/input';
import { cn } from '@workspace/ui/lib';
import { ApiError, type Country } from '@workspace/ui/types';
import { X } from 'lucide-react';
import Image from 'next/image';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useCreateCardRequest, useUnavailableCardCountries } from '@/hooks/use-cards';
import { useCountries } from '@/hooks/use-countries';

// Zod schemas for validation
const personalInfoSchema = z.object({
  firstName: z.string().min(1, 'Required').min(2, 'Must be at least 2 characters'),
  lastName: z.string().min(1, 'Required').min(2, 'Must be at least 2 characters'),
  title: z.string().min(1, 'Required'),
  gender: z.string().min(1, 'Required'),
  dateOfBirth: z
    .date({
      message: 'Required',
    })
    .refine(
      (date) => {
        const today = new Date();
        const eighteenYearsAgo = new Date(
          today.getFullYear() - 18,
          today.getMonth(),
          today.getDate()
        );
        return date <= eighteenYearsAgo;
      },
      {
        message: 'You must be at least 18 years old',
      }
    ),
  phoneNumber: z.object({
    id: z.string().optional(),
    prefix: z.string().min(1, 'Required'),
    number: z.string().min(1, 'Required').regex(/^\d+$/, 'Phone number must contain only digits'),
    iso_code: z.string().optional(),
    image: z
      .object({
        path: z.string(),
        alt: z.string(),
      })
      .optional(),
  }),
  countryOfBirth: z.string().min(1, 'Required'),
});

const deliveryAddressSchema = z.object({
  address: z.string().min(1, 'Required').min(5, 'Must be at least 5 characters'),
  city: z.string().min(1, 'Required').min(2, 'Must be at least 2 characters'),
  state: z.string().min(1, 'Required').min(2, 'Must be at least 2 characters'),
  zipCode: z.string().min(1, 'Required').min(3, 'Must be at least 3 characters'),
  country: z.string().min(1, 'Required'),
});

const fullSchema = personalInfoSchema.merge(deliveryAddressSchema);
const deliveryAddressWithoutStateSchema = deliveryAddressSchema.omit({ state: true });

type FullFormData = z.infer<typeof fullSchema>;

function isDeliveryAddressMissingOnlyState(values: Partial<FullFormData>): boolean {
  if (deliveryAddressSchema.safeParse(values).success) return false;
  return deliveryAddressWithoutStateSchema.safeParse(values).success;
}

function MissingDetailsNote() {
  return (
    <p className="text-sm leading-5 text-red-500 dark:text-red-300">
      Some details are missing. Edit to complete them.
    </p>
  );
}

interface DeliveryAddressSummaryProps {
  address: string;
  isComplete: boolean;
  showKnownAddress: boolean;
}

function DeliveryAddressSummary({
  address,
  isComplete,
  showKnownAddress,
}: DeliveryAddressSummaryProps) {
  return (
    <>
      {showKnownAddress ? (
        <p className="text-grey-900 dark:text-grey-400 text-base leading-6">{address}</p>
      ) : null}
      {isComplete ? null : <MissingDetailsNote />}
    </>
  );
}

interface SummarySectionProps {
  title: string;
  children: React.ReactNode;
  onEdit: () => void;
}

function SummarySection({ title, children, onEdit }: SummarySectionProps) {
  return (
    <div className="bg-grey-0 dark:bg-grey-900 flex w-full flex-col gap-3 rounded-2xl px-4 py-5">
      <div className="flex w-full items-center justify-between gap-7">
        <p className="text-neutral-200 dark:text-neutral-100 min-w-0 flex-1 text-base leading-6 font-medium">
          {title}
        </p>
        <Button type="button" variant="secondary" size="xs" onClick={onEdit}>
          Edit
        </Button>
      </div>
      {children}
    </div>
  );
}

interface ReviewCardInfoDialogProps {
  isReviewDialogOpen: boolean;
  setIsReviewDialogOpen: (open: boolean) => void;
  onSuccess?: () => void;
  prefillData?: Partial<FullFormData> | null;
}

const titleOptions = [
  { value: 'Mr', name: 'Mr' },
  { value: 'Mrs', name: 'Mrs' },
  { value: 'Ms', name: 'Ms' },
  { value: 'Dr', name: 'Dr' },
];

const genderOptions = [
  { value: 'male', name: 'Male' },
  { value: 'female', name: 'Female' },
  { value: 'other', name: 'Other' },
];

const personalFields = [
  'firstName',
  'lastName',
  'title',
  'gender',
  'dateOfBirth',
  'phoneNumber',
  'countryOfBirth',
] as const;

const addressFields = ['address', 'city', 'state', 'zipCode', 'country'] as const;

type DialogView = 'summary' | 'edit-personal' | 'edit-address';

function matchedOption(value: string | undefined, options: { value: string }[]) {
  if (!value) return value;
  return options.find((option) => option.value.toLowerCase() === value.toLowerCase())?.value ?? '';
}

function cleanPrefillData(prefillData: Partial<FullFormData> | null): Partial<FullFormData> | null {
  if (!prefillData) return null;

  return {
    ...prefillData,
    title: matchedOption(prefillData.title, titleOptions),
    gender: matchedOption(prefillData.gender, genderOptions),
  };
}

function toFormValues(data: Partial<FullFormData> | null): Partial<FullFormData> {
  return {
    firstName: data?.firstName || '',
    lastName: data?.lastName || '',
    title: data?.title || '',
    gender: data?.gender || '',
    dateOfBirth: data?.dateOfBirth || undefined,
    phoneNumber: {
      id: data?.phoneNumber?.id || '',
      prefix: data?.phoneNumber?.prefix || '',
      number: data?.phoneNumber?.number || '',
      iso_code: data?.phoneNumber?.iso_code || '',
      image: {
        path: data?.phoneNumber?.image?.path || '',
        alt: data?.phoneNumber?.image?.alt || '',
      },
    },
    countryOfBirth: data?.countryOfBirth || '',
    address: data?.address || '',
    city: data?.city || '',
    state: data?.state || '',
    zipCode: data?.zipCode || '',
    country: data?.country || '',
  };
}

function formatDate(date: Date, separator: string) {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${day}${separator}${month}${separator}${date.getFullYear()}`;
}

export function ReviewCardInfoDialog({
  isReviewDialogOpen,
  setIsReviewDialogOpen,
  onSuccess,
  prefillData = null,
}: ReviewCardInfoDialogProps) {
  const [view, setView] = useState<DialogView>('summary');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const editSnapshot = useRef<FullFormData | null>(null);
  const { data: countries } = useCountries();
  const createCardRequestMutation = useCreateCardRequest();
  const queryClient = useQueryClient();
  const {
    data: unavailableCardCountries,
    isSuccess: hasUnavailableCardCountries,
    isError: hasUnavailableCardCountriesError,
    isFetching: isFetchingUnavailableCardCountries,
    refetch: refetchUnavailableCardCountries,
  } = useUnavailableCardCountries('eps', isReviewDialogOpen);

  const cleanedPrefillData = useMemo(() => cleanPrefillData(prefillData), [prefillData]);
  const defaultValues = useMemo(() => toFormValues(cleanedPrefillData), [cleanedPrefillData]);

  const form = useForm<FullFormData>({
    resolver: zodResolver(fullSchema),
    mode: 'onTouched',
    defaultValues,
  });

  const openEdit = (nextView: Exclude<DialogView, 'summary'>) => {
    editSnapshot.current = form.getValues();
    setView(nextView);
  };

  const cancelEdit = () => {
    if (editSnapshot.current) form.reset(editSnapshot.current);
    setView('summary');
  };

  const saveSection = async (fields: readonly (keyof FullFormData)[]) => {
    const isValid = await form.trigger(fields);
    if (isValid) setView('summary');
  };

  const handleSubmit = async (data: FullFormData) => {
    setSubmitError(null);

    if (!hasUnavailableCardCountries || !unavailableCardCountries) return;

    if (unavailableCardCountries.includes(data.country.toLowerCase())) {
      form.setError('country', {
        message: 'Card delivery is unavailable in this country',
      });
      return;
    }

    // Find countries from the countries data
    const birthCountry = countries?.find(
      (country: Country) => country.iso_code.toLowerCase() === data.countryOfBirth
    );
    const deliveryCountry = countries?.find(
      (country: Country) => country.iso_code.toLowerCase() === data.country
    );

    if (!birthCountry || !deliveryCountry) {
      setSubmitError('Could not find countries for the selected country codes');
      return;
    }

    const cardRequestData = {
      first_name: data.firstName,
      last_name: data.lastName,
      gender: data.gender,
      title: data.title,
      birthday: formatDate(data.dateOfBirth, '-'),
      phone: `${data.phoneNumber.prefix}${data.phoneNumber.number}`,
      address: data.address,
      city: data.city,
      state: data.state,
      post_code: data.zipCode,
      country: deliveryCountry.iso_code.toUpperCase(),
      country_of_birth: birthCountry.iso_code.toUpperCase(),
      card_type: 'physical',
      currency: 'EUR',
      name_on_card: `${data.firstName} ${data.lastName}`.trim(),
    };

    createCardRequestMutation.mutate(
      {
        service: 'eps',
        requestData: cardRequestData,
      },
      {
        onSuccess: () => {
          trackWalletAction('card_ordered', {
            flow: 'card',
            result: 'success',
            currency: cardRequestData.currency,
          });
          queryClient.invalidateQueries({ queryKey: ['card-requests', 'eps'] });
          queryClient.invalidateQueries({ queryKey: ['native-cards', 'eps'] });
          onSuccess?.();
        },
        onError: (error) => {
          trackWalletAction('card_ordered', {
            flow: 'card',
            result: 'failure',
            currency: cardRequestData.currency,
            reason_code: toAnalyticsReasonCode(error),
          });
          const fieldMessages =
            error instanceof ApiError && error.errors ? Object.values(error.errors).flat() : [];

          setSubmitError(
            fieldMessages.length > 0
              ? fieldMessages.join(' ')
              : error?.message || 'An error occurred while submitting your card request.'
          );
        },
      }
    );
  };

  const handleClose = () => {
    setIsReviewDialogOpen(false);
    setView('summary');
    setSubmitError(null);
    form.reset(defaultValues);
  };

  const countryOptions = useMemo(
    () =>
      countries?.map((country: Country) => ({
        value: country.iso_code.toLowerCase(),
        name: country.name,
        image: {
          path: `https://i.nativeteams.com/assets/app/flags_code/${country.iso_code}.svg`,
          alt: country.name,
        },
      })) || [],
    [countries]
  );
  const unavailableCountryCodes = useMemo(
    () => new Set(unavailableCardCountries ?? []),
    [unavailableCardCountries]
  );
  const deliveryCountryOptions = useMemo(() => {
    if (!hasUnavailableCardCountries) return [];

    return countryOptions.filter((country) => !unavailableCountryCodes.has(country.value));
  }, [countryOptions, hasUnavailableCardCountries, unavailableCountryCodes]);

  useEffect(() => {
    if (cleanedPrefillData && isReviewDialogOpen) form.reset(defaultValues);
  }, [cleanedPrefillData, isReviewDialogOpen, defaultValues, form]);

  const values = form.watch();
  const isPersonalComplete = personalInfoSchema.safeParse(values).success;
  const isAddressComplete = deliveryAddressSchema.safeParse(values).success;
  const isOnlyStateMissing = isDeliveryAddressMissingOnlyState(values);
  const canOrder = isPersonalComplete && isAddressComplete;

  const countryName = (isoCode?: string) =>
    countries?.find((country: Country) => country.iso_code.toLowerCase() === isoCode)?.name;

  const formattedBirthday = values.dateOfBirth ? formatDate(values.dateOfBirth, '/') : '';
  const genderLabel = genderOptions.find((option) => option.value === values.gender)?.name;
  const hasPersonalName = Boolean(values.firstName || values.lastName);
  const personalSummary = hasPersonalName
    ? [values.title && `${values.title}.`, values.firstName, values.lastName]
        .filter(Boolean)
        .join(' ')
    : '';
  const personalDetails = [
    genderLabel,
    formattedBirthday,
    countryName(values.countryOfBirth),
    [values.phoneNumber?.prefix, values.phoneNumber?.number].filter(Boolean).join(' '),
  ].filter(Boolean);
  const addressSummary = [
    values.address,
    values.city,
    values.state,
    values.zipCode,
    countryName(values.country),
  ]
    .filter(Boolean)
    .join(', ');

  const preventEnterSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') event.preventDefault();
  };

  useEffect(() => {
    if (!isReviewDialogOpen || !cleanedPrefillData || !hasUnavailableCardCountries) return;

    const selectedCountry = form.getValues('country');

    if (selectedCountry && unavailableCountryCodes.has(selectedCountry)) {
      form.setValue('country', '', { shouldDirty: true, shouldValidate: true });
    }
  }, [
    cleanedPrefillData,
    form,
    hasUnavailableCardCountries,
    isReviewDialogOpen,
    unavailableCountryCodes,
  ]);

  return (
    <Dialog open={isReviewDialogOpen} onOpenChange={handleClose}>
      <DialogContent
        size="md"
        className="sm:max-w-[600px] [&>.dialog-scrollbar]:overflow-y-auto"
        hideCloseButton
      >
        <DialogHeader className="sr-only">
          <DialogTitle>
            {view === 'edit-personal'
              ? 'Edit personal information'
              : view === 'edit-address'
                ? 'Edit shipping address'
                : 'Order your Native Teams card'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex w-full justify-end">
          <DialogClose asChild>
            <Button
              type="button"
              variant="primary-icon"
              className="[&[data-variant*=icon]]:h-8 [&[data-variant*=icon]]:w-8 [&[data-variant*=icon]_svg]:h-[24px] [&[data-variant*=icon]_svg]:w-[24px]"
            >
              <X />
              <p className="sr-only">Close</p>
            </Button>
          </DialogClose>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            onKeyDown={preventEnterSubmit}
            className={cn('-mt-2.5 flex w-full flex-col', view === 'summary' ? 'gap-8' : 'gap-6')}
          >
            {view === 'summary' ? (
              <div className="flex w-full flex-col gap-6">
                <p className="text-neutral-200 dark:text-neutral-100 text-[32px] leading-10 font-bold">
                  Order your Native Teams card
                </p>
                <div className="flex w-full flex-col gap-3">
                  <Image
                    src="https://i.nativeteams.com/assets/app/wallet/cards/card-order-summary-photo.webp"
                    alt="Native Teams card"
                    width={1024}
                    height={300}
                    className="h-auto w-full rounded-3xl"
                  />
                  <SummarySection
                    title={personalSummary || 'Personal information'}
                    onEdit={() => openEdit('edit-personal')}
                  >
                    {isPersonalComplete ? (
                      <div className="flex w-full flex-wrap items-center gap-x-2.5">
                        {personalDetails.map((detail, index) => (
                          <div key={detail} className="flex items-center gap-2.5">
                            {index > 0 && (
                              <p className="text-grey-900 dark:text-grey-400 text-base leading-6">
                                •
                              </p>
                            )}
                            <p className="text-grey-900 dark:text-grey-400 text-base leading-6">
                              {detail}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <MissingDetailsNote />
                    )}
                  </SummarySection>
                  <SummarySection title="Delivery address" onEdit={() => openEdit('edit-address')}>
                    <DeliveryAddressSummary
                      address={addressSummary}
                      isComplete={isAddressComplete}
                      showKnownAddress={isAddressComplete || isOnlyStateMissing}
                    />
                  </SummarySection>
                </div>
              </div>
            ) : null}

            {view === 'edit-personal' ? (
              <div className="flex w-full flex-col gap-4">
                <div className="mb-2 flex w-full flex-col gap-5">
                  <p className="text-neutral-200 dark:text-neutral-100 text-[32px] leading-10 font-bold">
                    Edit personal information
                  </p>
                  <p className="text-neutral-200 dark:text-neutral-100 text-xl leading-6 font-medium">
                    Review your personal details
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Type here" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Type here" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Combobox
                          options={titleOptions}
                          value={field.value || ''}
                          onChange={(_, option) => field.onChange(option?.value || '')}
                          onBlur={field.onBlur}
                          placeholder="Select"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Gender</FormLabel>
                      <FormControl>
                        <Combobox
                          options={genderOptions}
                          value={field.value || ''}
                          onChange={(_, option) => field.onChange(option?.value || '')}
                          onBlur={field.onBlur}
                          placeholder="Select"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of birth</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          error={!!form.formState.errors.dateOfBirth}
                          disabledDates={(date) => {
                            const today = new Date();
                            const eighteenYearsAgo = new Date(
                              today.getFullYear() - 18,
                              today.getMonth(),
                              today.getDate()
                            );
                            return date > eighteenYearsAgo || date > today;
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="countryOfBirth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <Combobox
                          showFilter
                          options={countryOptions}
                          value={field.value || ''}
                          onChange={(_, option) => field.onChange(option?.value || '')}
                          onBlur={field.onBlur}
                          placeholder="Select"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone number</FormLabel>
                      <FormControl>
                        <PhoneNumberSelect
                          {...field}
                          countries={countries || []}
                          aria-invalid={!!form.formState.errors.phoneNumber}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : null}

            {view === 'edit-address' ? (
              <div className="flex w-full flex-col gap-4">
                <div className="mb-2 flex w-full flex-col gap-5">
                  <p className="text-neutral-200 dark:text-neutral-100 text-[32px] leading-10 font-bold">
                    Edit shipping address
                  </p>
                  <p className="text-neutral-200 dark:text-neutral-100 text-xl leading-6 font-medium">
                    Card delivery address
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Type here" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Type here" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal code/ZIP</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Type here" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Region/State/Province</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Type here" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <Combobox
                          showFilter
                          options={deliveryCountryOptions}
                          value={field.value || ''}
                          disabled={!hasUnavailableCardCountries}
                          onChange={(_, option) => field.onChange(option?.value || '')}
                          onBlur={field.onBlur}
                          placeholder="Select"
                        />
                      </FormControl>
                      {hasUnavailableCardCountriesError && (
                        <div className="space-y-2">
                          <p className="text-sm text-red-700">
                            Unable to load countries available for card delivery.
                          </p>
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                              void refetchUnavailableCardCountries();
                            }}
                            isLoading={isFetchingUnavailableCardCountries}
                            loadingText="Retrying"
                          >
                            Retry
                          </Button>
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : null}

            {submitError ? (
              <div className="w-full rounded-2xl border border-red-200 bg-red-50 p-3 dark:border-red-500/40 dark:bg-red-500/10">
                <p className="text-sm font-medium text-red-600 dark:text-red-300">
                  Error submitting card request
                </p>
                <p className="mt-1 text-sm text-red-500 dark:text-red-400">{submitError}</p>
              </div>
            ) : null}

            {view === 'summary' ? (
              <Button
                type="submit"
                className="w-full"
                disabled={!canOrder || !hasUnavailableCardCountries}
                isLoading={createCardRequestMutation.isPending}
                loadingText="Ordering card"
              >
                Order card
              </Button>
            ) : (
              <div className="flex w-full flex-col gap-4">
                <Button
                  type="button"
                  className="w-full"
                  onClick={() =>
                    saveSection(view === 'edit-personal' ? personalFields : addressFields)
                  }
                >
                  Save changes
                </Button>
                <Button type="button" variant="secondary" className="w-full" onClick={cancelEdit}>
                  Cancel
                </Button>
              </div>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
